import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  getPriorityInfo,
  statusTextToNumber,
  statusNumberToText,
} from "./taskUtils";
import axiosClient from "../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import WorkItemIcon from "./Workitemicon";

const COLUMNS = [
  { key: 1, label: "New",         icon: "fas fa-circle-notch", accent: "#6B7280", bg: "#F3F4F6" },
  { key: 2, label: "In Progress", icon: "fas fa-spinner",       accent: "#2563EB", bg: "#EFF6FF" },
  { key: 3, label: "Closed",      icon: "fas fa-check-circle",  accent: "#059669", bg: "#ECFDF5" },
  { key: 4, label: "Blocked",     icon: "fas fa-ban",           accent: "#DC2626", bg: "#FEF2F2" },
  // { key: 5, label: "On Hold",     icon: "fas fa-pause-circle",  accent: "#D97706", bg: "#FFFBEB" },
  // { key: 6, label: "Resolved",    icon: "fas fa-check-double",  accent: "#7C3AED", bg: "#F5F3FF" },
];

const TYPE_COLOR_MAP = {
  bug:          "#EF4444",
  "user-story": "#3B82F6",
  "user story": "#3B82F6",
  userstory:    "#3B82F6",
  feature:      "#F59E0B",
  task:         "#10B981",
  epic:         "#8B5CF6",
};

function getTypeColor(type) {
  return TYPE_COLOR_MAP[(type || "").toLowerCase().trim()] || "#6B7280";
}

export const KanbanBoard = ({
  tasks: initialTasks,
  usersList,
  onOpenDetail,
  currentUserId,
}) => {
  const [tasks, setTasks] = useState(initialTasks);
  const [dragOverCol, setDragOverCol] = useState(null);
  const dragTask = useRef(null);
  const dragCounters = useRef({});
  const latestTasksRef = useRef(initialTasks);

  React.useEffect(() => {
    setTasks(initialTasks);
    latestTasksRef.current = initialTasks;
  }, [initialTasks]);

  const columns = COLUMNS.map((col) => ({
    ...col,
    tasks: tasks.filter((t) => statusTextToNumber(t.status) === col.key),
  }));

  const handleDragStart = (e, task) => {
    dragTask.current = task;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.45";
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    dragTask.current = null;
    setDragOverCol(null);
    dragCounters.current = {};
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e, colKey) => {
    e.preventDefault();
    dragCounters.current[colKey] = (dragCounters.current[colKey] || 0) + 1;
    if (dragCounters.current[colKey] === 1) setDragOverCol(colKey);
  };

  const handleDragLeave = (e, colKey) => {
    dragCounters.current[colKey] = (dragCounters.current[colKey] || 1) - 1;
    if (dragCounters.current[colKey] === 0) setDragOverCol(null);
  };

  const handleDrop = async (e, targetColKey) => {
  e.preventDefault();
  
  const task = dragTask.current;   // ← capture FIRST
  dragTask.current = null;         // ← THEN clear
  dragCounters.current[targetColKey] = 0;
  setDragOverCol(null);

    if (!task) return;

    const currentStatus = statusTextToNumber(task.status);
    if (currentStatus === targetColKey) return;

    const newStatusText = statusNumberToText(targetColKey);
    const previousTasks = latestTasksRef.current;

    setTasks((prev) => {
      const updated = prev.map((t) =>
        t.id === task.id ? { ...t, status: newStatusText } : t,
      );
      latestTasksRef.current = updated;
      return updated;
    });

    try {
      const payload = {
        Id: String(task.id),
        ParentId: String(task.parentid ?? 0),
        TypeId: task.typeId || "0",
        AssignedBy: String(task.assignedById || task.assignedBy || 0),
        AssignedTo: String(task.assignedToId || task.assignedTo || 0),
        Subject: task.subject?.trim() || "Untitled",
        Description: task.description?.trim() || "",
        Priority:
          typeof task.priority === "string"
            ? ({ High: 1, Medium: 2, Low: 3 }[task.priority] ?? 2)
            : task.priority,
        IsRead: Boolean(task.isRead),
        Status: targetColKey,
        DueDate: task.dueDate ? new Date(task.dueDate).toISOString() : new Date().toISOString(),
        StartDate: task.startDate ? new Date(task.startDate).toISOString() : new Date().toISOString(),
        EndDate: task.endDate ? new Date(task.endDate).toISOString() : new Date().toISOString(),
        Efforts: task.efforts ?? 0,
        IsDeleted: Boolean(task.isDeleted),
        CreatedBy: String(task.createdBy || "0"),
        UpdatedBy: String(currentUserId || "0"),
      };

      await axiosClient.put(`/api/Task/${task.id}`, payload);
      toast.success(
        `Moved to "${COLUMNS.find((c) => c.key === targetColKey)?.label}"`,
        { autoClose: 2000 },
      );
    } catch {
      setTasks(previousTasks);
      latestTasksRef.current = previousTasks;
      toast.error("Failed to update task status");
    }
  };

  return (
    <div className="flex gap-5 overflow-x-auto pb-4 items-start">
      {columns.map((col) => {
        const isOver = dragOverCol === col.key;
        return (
          <section
            key={col.key}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, col.key)}
            onDragLeave={(e) => handleDragLeave(e, col.key)}
            onDrop={(e) => handleDrop(e, col.key)}
            aria-label={`${col.label} column`}
            style={{
              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
              borderColor: isOver ? col.accent : "#E5E7EB",
              boxShadow: isOver
                ? `0 0 0 2px ${col.accent}55, 0 4px 20px rgba(0,0,0,0.08)`
                : "0 1px 3px rgba(0,0,0,0.06)",
              background: isOver
                ? col.bg
                : "linear-gradient(to bottom,#F9FAFB,#F3F4F6)",
            }}
            className="flex-shrink-0 w-72 border rounded-xl overflow-hidden flex flex-col max-h-[calc(100vh-220px)]"
          >
            {/* Column header */}
            <div
              className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100 sticky top-0 z-10"
              style={{ borderTop: `3px solid ${col.accent}` }}
            >
              <span className="font-bold text-gray-800 flex items-center gap-2 text-sm">
                <i className={col.icon} style={{ color: col.accent }} />
                {col.label}
              </span>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-md"
                style={{ background: col.bg, color: col.accent }}
              >
                {col.tasks.length}
              </span>
            </div>

            {/* Cards */}
            <div className="p-3 overflow-y-auto flex flex-col gap-2.5 flex-1">
              {isOver && col.tasks.length === 0 && (
                <div
                  className="rounded-lg border-2 border-dashed flex items-center justify-center py-6 text-xs font-semibold"
                  style={{
                    borderColor: col.accent,
                    color: col.accent,
                    background: col.bg,
                    pointerEvents: "none",
                  }}
                >
                  <i className="fas fa-plus mr-1.5" />{" "}
                  Drop here
                </div>
              )}

              {col.tasks.length === 0 && !isOver && (
                <div className="text-center py-10 text-gray-400">
                  <i className="fas fa-inbox text-2xl opacity-30 block mb-2" />
                  <p className="text-xs font-medium">No tasks</p>
                  <p className="text-xs opacity-70 mt-0.5">Drag cards here</p>
                </div>
              )}

              {col.tasks.map((task) => {
                const assignee = usersList.find((u) => u.id === task.assignedToId);
                const initials = assignee
                  ? `${assignee.first_name[0]}${assignee.last_name[0]}`
                  : "?";
                const priorityInfo = getPriorityInfo(task.priority);
                const isOverdue =
                  task.dueDate &&
                  new Date(task.dueDate) < new Date() &&
                  col.key !== 3;
                const typeColor = getTypeColor(task.type);

                return (
                  <button
                    key={task.id}
                    type="button"
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onOpenDetail(task)}
                    className="bg-white border border-gray-200 rounded-lg p-3 cursor-grab active:cursor-grabbing select-none w-full text-left"
                    style={{
                      transition: "box-shadow 0.15s, border-color 0.15s, transform 0.15s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.10)";
                      e.currentTarget.style.borderColor = "#93C5FD";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                      e.currentTarget.style.borderColor = "#E5E7EB";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {/* Top row: type icon + label + drag handle */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <WorkItemIcon type={task.type || "task"} size={13} />
                        {task.type && (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wide"
                            style={{ color: typeColor }}
                          >
                            {task.type}
                          </span>
                        )}
                      </div>
                      <i
                        className="fas fa-grip-vertical text-gray-300"
                        style={{ fontSize: 11 }}
                        title="Drag to move"
                      />
                    </div>

                    {/* Subject */}
                    <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2.5">
                      {task.subject}
                    </p>

                    {/* Bottom row: priority + assignee avatar */}
                    <div className="flex items-center justify-between">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${priorityInfo.color}`}
                      >
                        {priorityInfo.text}
                      </span>
                      <div
                        className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                        style={{ background: col.accent }}
                        title={
                          assignee
                            ? `${assignee.first_name} ${assignee.last_name}`
                            : "Unassigned"
                        }
                      >
                        {initials}
                      </div>
                    </div>

                    {task.dueDate && (
                      <div
                        className={`text-xs mt-2 flex items-center gap-1 ${
                          isOverdue ? "text-red-500 font-semibold" : "text-gray-400"
                        }`}
                      >
                        <i
                          className={`fas ${isOverdue ? "fa-circle-exclamation" : "fa-calendar"} text-[10px]`}
                        />
                        {new Date(task.dueDate).toLocaleDateString()}
                        {isOverdue && <span className="ml-1">Overdue</span>}
                      </div>
                    )}
                  </button>
                );
              })}

              <div style={{ minHeight: "40px", flexShrink: 0 }} />
            </div>
          </section>
        );
      })}
    </div>
  );
};

KanbanBoard.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      status: PropTypes.string,
      priority: PropTypes.string,
      subject: PropTypes.string,
      type: PropTypes.string,
      description: PropTypes.string,
      assignedToId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      assignedById: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      assignedBy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      parentid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      typeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dueDate: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      efforts: PropTypes.number,
      isRead: PropTypes.bool,
      isDeleted: PropTypes.bool,
      createdBy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
    }),
  ).isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};